import { useState, useEffect } from "react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Users, DollarSign, TrendingUp, Copy, Check } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";

const API_URL = process.env.REACT_APP_BACKEND_URL + "/api";

export default function ReferralsPage() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const res = await axios.get(`${API_URL}/referrals/stats`);
      setStats(res.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const copyCode = () => {
    if (user?.referral_code) {
      navigator.clipboard.writeText(user.referral_code);
      setCopied(true);
      toast.success("Code copié !");
      setTimeout(() => setCopied(false), 2000);
    }
  };

  if (loading) return <div className="min-h-screen bg-gray-100 flex items-center justify-center">Chargement...</div>;

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      
      <div className="container mx-auto px-4 py-8 max-w-6xl">
        <div className="mb-8">
          <h1 className="text-3xl font-bold mb-2">Programme de Parrainage</h1>
          <p className="text-gray-600">Gagnez de l'argent en invitant vos amis sur BuyMoreEasy.</p>
        </div>

        {/* Overview Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <Card className="bg-gradient-to-br from-orange-500 to-red-500 text-white border-0">
            <CardContent className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="font-semibold text-lg">Gains Totaux</h3>
                <DollarSign className="h-6 w-6 opacity-80" />
              </div>
              <div className="text-4xl font-bold mb-2">{stats?.total_earned.toFixed(2)} HTG</div>
              <p className="text-sm opacity-90">Versés sur votre portefeuille</p>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="font-semibold text-gray-700">Filleuls (Niveau 1)</h3>
                <Users className="h-6 w-6 text-blue-500" />
              </div>
              <div className="text-3xl font-bold mb-2">{stats?.level1_count}</div>
              <p className="text-sm text-gray-500">Commission : 1.5%</p>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="font-semibold text-gray-700">Sous-Filleuls (Niveau 2)</h3>
                <TrendingUp className="h-6 w-6 text-green-500" />
              </div>
              <div className="text-3xl font-bold mb-2">{stats?.level2_count}</div>
              <p className="text-sm text-gray-500">Commission : 0.5%</p>
            </CardContent>
          </Card>
        </div>

        {/* Share Section */}
        <Card className="mb-8 border-orange-200 bg-orange-50">
          <CardContent className="p-6 flex flex-col md:flex-row items-center justify-between gap-6">
            <div>
              <h3 className="text-lg font-bold text-orange-800 mb-1">Partagez votre code unique</h3>
              <p className="text-orange-700 text-sm">Donnez ce code à vos amis lors de leur inscription.</p>
            </div>
            <div className="flex items-center gap-2 bg-white p-2 rounded-lg border border-orange-200">
              <code className="text-xl font-mono font-bold px-4">{user?.referral_code}</code>
              <Button size="icon" variant="ghost" onClick={copyCode}>
                {copied ? <Check className="h-5 w-5 text-green-600" /> : <Copy className="h-5 w-5 text-gray-600" />}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Details Tabs */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Referrals List */}
          <Card>
            <CardHeader>
              <CardTitle>Mes Filleuls</CardTitle>
            </CardHeader>
            <CardContent>
              {stats?.referrals.length > 0 ? (
                <div className="space-y-4">
                  {stats.referrals.map((ref) => (
                    <div key={ref.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold ${ref.level === 1 ? 'bg-blue-500' : 'bg-green-500'}`}>
                          N{ref.level}
                        </div>
                        <div>
                          <p className="font-medium text-sm">{ref.name}</p>
                          <p className="text-xs text-gray-500">Inscrit le {new Date(ref.date).toLocaleDateString()}</p>
                        </div>
                      </div>
                      <Badge variant="outline">Niveau {ref.level}</Badge>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-center text-gray-500 py-8">Aucun filleul pour le moment.</p>
              )}
            </CardContent>
          </Card>

          {/* Commissions History */}
          <Card>
            <CardHeader>
              <CardTitle>Historique des Commissions</CardTitle>
            </CardHeader>
            <CardContent>
              {stats?.commissions.length > 0 ? (
                <div className="space-y-4">
                  {stats.commissions.map((comm, idx) => (
                    <div key={idx} className="flex items-center justify-between p-3 border-b last:border-0">
                      <div>
                        <p className="font-medium text-green-600">+{comm.amount.toFixed(2)} HTG</p>
                        <p className="text-xs text-gray-500">{new Date(comm.date).toLocaleDateString()}</p>
                      </div>
                      <Badge variant="secondary" className="text-xs">
                        Niveau {comm.level} ({comm.rate * 100}%)
                      </Badge>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-center text-gray-500 py-8">Aucune commission reçue.</p>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
      <Footer />
    </div>
  );
}
